import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetTenderFiltersQuery, useListTendersQuery } from "@/state/tendersApi";
import type { Tender } from "@/types/tenders";
import TenderDetailModal from "./TenderDetailModal";

type Props = {
  onRequestSupport?: (reference: string) => void;
}

export default function TendersList({ onRequestSupport }: Props) {
  const { t } = useTranslation();
  const [selectedSector, setSelectedSector] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [activeTender, setActiveTender] = useState<Tender | null>(null);

  const { data: filtersData } = useGetTenderFiltersQuery();
  const { data: listData, isLoading } = useListTendersQuery({
    sector: selectedSector || undefined,
    country: selectedCountry || undefined,
  });

  const sectors = filtersData?.data?.sectors ?? [];
  const countries = filtersData?.data?.countries ?? [];
  const tenders = listData?.data ?? [];
  const total = listData?.meta?.total ?? 0;

  const statusColor: Record<string, string> = {
    "Open": "bg-green-100 text-green-700",
    "Closing Soon": "bg-orange-100 text-orange-700",
    "Closed": "bg-gray-100 text-gray-500",
  };

  const getDaysLeft = (deadline: string) => {
    const diff = Math.ceil((new Date(deadline).getTime() - Date.now()) / 86400000);
    if (diff < 0) return t("listColStatus");
    if (diff === 0) return t("listToday");
    return `${diff}${t("days")} ${t("listLeft")}`;
  };

  return (
    <section id="tender-list" className="py-24 bg-white">
      <div className="w-full px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-12">
          <div>
            <p className="text-[#00C2C2] text-xs font-semibold tracking-widest uppercase mb-3">{t("listTag")}</p>
            <h2 className="text-4xl font-bold text-[#0A1628]">{t("listTitle1")} <span className="text-[#00C2C2]">{t("listTitle2")}</span></h2>
            <p className="text-gray-500 text-base mt-3 max-w-xl">{t("listDesc")}</p>
          </div>
          <div className="text-sm text-gray-400">{total} {t("listCount", { count: total })}</div>
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center text-[#00C2C2]"><i className="ri-filter-3-line text-sm" /></div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t("filterBy")}</span>
          </div>
          <select value={selectedCountry} onChange={(e) => setSelectedCountry(e.target.value)} className="text-sm border border-gray-200 rounded-full px-4 py-2 bg-[#f8fafc] text-[#0A1628] focus:outline-none focus:border-[#00C2C2] cursor-pointer transition-colors">
            <option value="">{t("listFilterCountry")}</option>
            {countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={selectedSector} onChange={(e) => setSelectedSector(e.target.value)} className="text-sm border border-gray-200 rounded-full px-4 py-2 bg-[#f8fafc] text-[#0A1628] focus:outline-none focus:border-[#00C2C2] cursor-pointer transition-colors">
            <option value="">{t("listFilterSector")}</option>
            {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {(selectedSector || selectedCountry) && (
            <button onClick={() => { setSelectedSector(""); setSelectedCountry(""); }} className="text-xs text-[#00C2C2] font-semibold hover:underline flex items-center gap-1 cursor-pointer whitespace-nowrap">
              <i className="ri-close-line" /> {t("listClear")}
            </button>
          )}
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-100">
          <table className="w-full text-sm">
            <thead className="bg-[#f8fafc] text-xs text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-5 py-4 font-semibold">{t("listColTitle")}</th>
                <th className="text-left px-4 py-4 font-semibold hidden md:table-cell">{t("listColSector")}</th>
                <th className="text-left px-4 py-4 font-semibold hidden lg:table-cell">{t("listColCountry")}</th>
                <th className="text-left px-4 py-4 font-semibold">{t("listColDeadline")}</th>
                <th className="text-left px-4 py-4 font-semibold hidden sm:table-cell">{t("listColStatus")}</th>
                <th className="px-4 py-4" />
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-t border-gray-50">
                      <td className="px-5 py-4">
                        <div className="animate-pulse">
                          <div className="h-4 bg-gray-100 rounded w-3/4 mb-1" />
                          <div className="h-3 bg-gray-100 rounded w-1/2" />
                        </div>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell"><div className="animate-pulse h-5 bg-gray-100 rounded-full w-20" /></td>
                      <td className="px-4 py-4 hidden lg:table-cell"><div className="animate-pulse h-4 bg-gray-100 rounded w-16" /></td>
                      <td className="px-4 py-4"><div className="animate-pulse h-4 bg-gray-100 rounded w-24" /></td>
                      <td className="px-4 py-4 hidden sm:table-cell"><div className="animate-pulse h-5 bg-gray-100 rounded-full w-14" /></td>
                      <td className="px-4 py-4" />
                    </tr>
                  ))
                : tenders.map((tender, i) => (
                    <tr key={tender.id} className={`border-t border-gray-50 hover:bg-[#f8fafc] transition-colors ${i % 2 === 0 ? "" : "bg-white"}`}>
                      <td className="px-5 py-4">
                        <p className="font-semibold text-[#0A1628] text-sm leading-snug max-w-xs">{tender.title}</p>
                        <p className="text-xs text-gray-400 mt-0.5 hidden sm:block">{tender.client}</p>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#0A1628]/10 text-[#00C2C2] whitespace-nowrap">{tender.sector}</span>
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell text-sm text-gray-600 whitespace-nowrap">{tender.country}</td>
                      <td className="px-4 py-4">
                        <p className="text-sm text-[#0A1628] font-medium whitespace-nowrap">{new Date(tender.deadline).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
                        <p className="text-xs text-orange-500 font-semibold mt-0.5">{getDaysLeft(tender.deadline)}</p>
                      </td>
                      <td className="px-4 py-4 hidden sm:table-cell">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${statusColor[tender.status] || "bg-gray-100 text-gray-500"}`}>{tender.status}</span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button
                          onClick={() => setActiveTender(tender)}
                          className="text-xs font-bold text-[#00C2C2] hover:underline whitespace-nowrap cursor-pointer flex items-center gap-1"
                        >
                          {t("listView")} <i className="ri-arrow-right-line" />
                        </button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
          {!isLoading && tenders.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <p className="font-semibold text-[#0A1628]">{t("listNoResults")}</p>
              <p className="text-sm mt-1">{t("listNoResultsHint")}</p>
            </div>
          )}
        </div>
      </div>

      {activeTender && <TenderDetailModal tender={activeTender} onClose={() => setActiveTender(null)} onRequestSupport={onRequestSupport} />}
    </section>
  );
}
